<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useSeo } from '@/composables/useSeo'
import { registry, categories } from '@/calculators/registry'
import PageLayout from '@/components/layout/PageLayout.vue'
import CalculatorCard from '@/components/home/CalculatorCard.vue'
import Icon from '@/components/icons/Icon.vue'

useSeo({
  title: 'All South African Calculators',
  description:
    'Browse every free South African calculator: salary and PAYE, VAT, fuel, bond repayments, transfer duty, solar ROI, cars and rental yield.',
  path: '/calculators',
})

const route = useRoute()
const activeCategory = ref((route.query.category as string) || 'all')

const categoryMap = computed(() =>
  Object.fromEntries(categories.map((c) => [c.id, { label: c.label, icon: c.icon }]))
)

const filteredCalculators = computed(() => {
  if (activeCategory.value === 'all') return registry
  return registry.filter((c) => c.category === activeCategory.value)
})
</script>

<template>
  <PageLayout
    title="All calculators"
    description="Find the right tool for your South African financial or practical calculation."
    :breadcrumbs="[{ label: 'Calculators' }]"
  >
    <div class="filters" role="group" aria-label="Filter calculators by category">
      <button
        v-for="category in [
          { id: 'all', label: 'All', icon: 'calculator' as const },
          ...categories,
        ]"
        :key="category.id"
        class="filter-chip"
        :class="{ 'filter-chip--active': activeCategory === category.id }"
        @click="activeCategory = category.id"
      >
        <Icon :name="category.icon" :size="14" />
        {{ category.label }}
      </button>
    </div>

    <div v-if="filteredCalculators.length > 0" class="grid">
      <CalculatorCard
        v-for="calc in filteredCalculators"
        :key="calc.id"
        :calc="calc"
        :category-label="categoryMap[calc.category]?.label"
        :category-icon="categoryMap[calc.category]?.icon"
      />
    </div>
    <p v-else class="empty">No calculators match this category yet.</p>
  </PageLayout>
</template>

<style scoped>
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-full);
  background-color: var(--color-surface);
  color: var(--color-text);
  font-size: var(--text-sm);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    transform 0.05s ease;
}

.filter-chip:hover {
  border-color: var(--color-primary-light);
  transform: translateY(-1px);
}

.filter-chip--active {
  background-color: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.grid {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}

.empty {
  color: var(--color-text-muted);
}
</style>
